/**
 * One date range for the whole app, with a compare period (SellerMate W0.3).
 *
 * Before this, every PPC page kept its own range (`useDateRange` +
 * `<DateRangePicker>` in the page), so Campaigns could show last 30 days while
 * Targets showed last week and nothing said so. Now a page OPTS IN with
 * `useAppDateRange()` and drops its own picker: the range lives here, once, and
 * the header control (components/AppDateRangeControl.tsx) edits it and reads
 * "current ▸ compared to".
 *
 * What is kept from DateRangePicker.tsx, on purpose:
 *   - the same presets (a preset's dates are always the window its name says;
 *     days Amazon has not reported are cut, not excluded);
 *   - `clampTo: 'ppc'`: attributed PPC pages still get the window cut
 *     to the reported days (the latest report day stands in when none is
 *     reported yet; `clampRange`), as the third value;
 *   - the storage rules: a single-day pick is never remembered, a preset's
 *     dates are re-derived on load so "Last 30 days" always ends today. The
 *     key is its OWN (`adbetter.dateRange.app`), not the picker's old shared
 *     default `adbetter.dateRange`: the pages that rendered their own picker
 *     without a storageKey (Ad Groups, Placements, Product Ads, Products, Ads
 *     Overview) wrote each of their picks there, which silently moved this
 *     range too. Since PR 3 (C7) DateRangePicker REQUIRES a storageKey and
 *     has no shared default, so nothing writes that key any more. It is read
 *     once, on the first load, so the last pick still carries over
 *     (`loadAppRange`);
 *   - a page's `setRange` (a KPI-tile click) changes the range for this visit
 *     only; a pick in the header is also remembered.
 * What is new: the compare setting (lib/dateRangeCompare.ts), remembered under
 * `adbetter.dateRange.compare`, and the fourth value — the compare window,
 * always derived from the window the page actually fetches.
 *
 * Why a small store and not a React context provider: the header and every
 * opted-in page must read ONE value, the keep-alive tab panes (tabs/TabsHost)
 * all share it, and a module store needs no provider wrapped around the shell
 * (so it also works in tests and in any page rendered outside AppShell).
 *
 * The header only shows the control while an opted-in page is on screen: a
 * page registers in an effect, and a hidden tab pane's effects are unmounted
 * by React `Activity` (TabsHost rule 4), so a Campaigns tab in the background
 * does not put a date control over the Orders page.
 */
import { useEffect, useId, useMemo, useSyncExternalStore } from 'react'
import {
  clampRange, loadStoredRange, persistRange, rangeFromPreset,
  type DateRange, type EffectiveRange,
} from '../components/DateRangePicker'
import { usePpcFreshness } from './query/freshness'
import { todayYMD } from './format/date'
import {
  DEFAULT_COMPARE, compareWindow, parseCompareSetting,
  type CompareMode, type CompareSetting, type DateWindow,
} from './dateRangeCompare'

/** The app-wide pick. Its own key: see `loadAppRange`. Under the
 *  `adbetter.dateRange` prefix, so the picker's one-time lag migration covers
 *  it like every other range key. */
export const APP_RANGE_KEY = 'adbetter.dateRange.app'
/** The key every page picker without a storageKey used to write (the
 *  picker's old shared default; PR 3 removed it). Read ONCE, to carry the
 *  last pick over. */
export const LEGACY_SHARED_RANGE_KEY = 'adbetter.dateRange'
/** The compare setting. Under the same prefix, so DateRangePicker's one-time
 *  lag migration (which runs before the first read) covers it too. */
export const APP_COMPARE_KEY = 'adbetter.dateRange.compare'

export type ClampTo = 'ppc'
export type AppDateRangeOptions = {
  /** 'ppc' for the attributed PPC pages: the fetched window is cut to the
   *  reported days; the latest report day stands in when none is reported
   *  (see DateRangePicker `clampRange`). */
  clampTo?: ClampTo
}
/** What a page passes `useAppDateRange`: the store options plus `enabled`. */
export type UseAppDateRangeOptions = AppDateRangeOptions & {
  /** False while the page is on a view that does not use the range (Alerts'
   *  other tabs): the header then shows no date control. Default true. */
  enabled?: boolean
}

/** The comparison a page should draw: the setting and its resolved window. */
export type AppCompare = {
  mode: CompareMode
  setting: CompareSetting
  /** The window to compare against; null for no comparison (or a custom
   *  comparison still missing its dates). */
  window: DateWindow | null
}

/** The chosen range and comparison. Replaced (never mutated) on every change,
 *  so it is a valid `useSyncExternalStore` snapshot. */
export type AppDateRangeCore = {
  range: DateRange
  compare: CompareSetting
  /** The account (Pacific) day `range`'s preset dates were derived on. */
  day: string
}

/** Which opted-in pages are on screen right now (the header reads this). */
export type AppDateRangeRegistry = {
  count: number
  /** 'ppc' when any on-screen consumer clamps to the PPC report day. */
  clampTo: ClampTo | null
  /** True while something on screen DRAWS the comparison (the Campaigns
   *  period tiles, through `useAppCompare`). The header offers "compared to"
   *  only then: on Targets or Search Terms it promised a comparison that
   *  nothing on the page showed. */
  compare: boolean
}

/**
 * The app-wide pick, from its own key. On the first load after the key moved
 * (nothing stored under it yet), the last pick is carried over ONCE from
 * DateRangePicker's shared default key, and written under the new key at once,
 * so no later page-picker write to the old key can reach this range again.
 */
export function loadAppRange(now?: Date): DateRange {
  let seeded = false
  try { seeded = localStorage.getItem(APP_RANGE_KEY) !== null } catch { /* blocked storage */ }
  if (seeded) return loadStoredRange(APP_RANGE_KEY, undefined, now)
  const carried = loadStoredRange(LEGACY_SHARED_RANGE_KEY, undefined, now)
  persistRange(APP_RANGE_KEY, carried)
  return carried
}

function readStoredCompare(): CompareSetting {
  try {
    const raw = localStorage.getItem(APP_COMPARE_KEY)
    return raw ? parseCompareSetting(JSON.parse(raw)) : DEFAULT_COMPARE
  } catch {
    // Blocked storage, a private window, or a hand-edited entry.
    return DEFAULT_COMPARE
  }
}

function writeStoredCompare(setting: CompareSetting): void {
  try { localStorage.setItem(APP_COMPARE_KEY, JSON.stringify(setting)) } catch { /* ignore */ }
}

/** The window a page fetches: the pick, cut to the reported PPC days
 *  (the latest report day stands in when none is reported) when the page clamps to it (DateRangePicker `clampRange`). */
export function effectiveWindow(
  range: DateRange, clampTo: ClampTo | null | undefined, dataThrough: string | null,
): EffectiveRange {
  return clampTo === 'ppc'
    ? clampRange(range, dataThrough)
    : { ...range, trimmed: false, dataThrough: null, standIn: false }
}

/** The comparison for a fetched window. */
export function resolveCompare(effective: DateWindow, setting: CompareSetting): AppCompare {
  return { mode: setting.mode, setting, window: compareWindow(effective, setting) }
}

/**
 * The fetched window and the compare window, from one pick. Pure: the page
 * hook and the header both resolve through these, so they can never disagree.
 */
export function resolveAppWindows(
  range: DateRange,
  compare: CompareSetting,
  clampTo: ClampTo | null | undefined,
  dataThrough: string | null,
): { effective: EffectiveRange; compare: AppCompare } {
  const effective = effectiveWindow(range, clampTo, dataThrough)
  return { effective, compare: resolveCompare(effective, compare) }
}

/** The store behind `useAppDateRange`. A factory so tests get a fresh one;
 *  the app uses the single instance below. Storage is read lazily, on first
 *  use, never at import. */
export function createAppDateRangeStore(now: () => Date = () => new Date()) {
  let core: AppDateRangeCore | null = null
  const consumers = new Map<string, AppDateRangeOptions>()
  const compareConsumers = new Set<string>()
  // True once a person (or a page's seed) set a comparison this session.
  let compareChosen = false
  let registry: AppDateRangeRegistry = { count: 0, clampTo: null, compare: false }
  const listeners = new Set<() => void>()
  const emit = () => { for (const l of listeners) l() }

  const today = () => todayYMD(now())

  function getCore(): AppDateRangeCore {
    if (!core) {
      core = {
        range: loadAppRange(now()),
        compare: readStoredCompare(),
        day: today(),
      }
    }
    return core
  }

  function compareIsUnset(): boolean {
    if (compareChosen) return false
    try { return localStorage.getItem(APP_COMPARE_KEY) === null } catch { return true }
  }

  function setCore(next: AppDateRangeCore): void {
    core = next
    emit()
  }

  /** Re-derive a preset's dates when the calendar day has turned: a tab left
   *  open overnight must not keep showing yesterday's "Last 30 days". */
  function refreshDay(): void {
    const c = getCore()
    const t = today()
    if (c.day === t) return
    const range = c.range.preset === 'custom'
      ? c.range
      : rangeFromPreset(c.range.preset, 0, now())
    setCore({ ...c, range, day: t })
  }

  function recomputeRegistry(): void {
    let clampTo: ClampTo | null = null
    for (const o of consumers.values()) if (o.clampTo === 'ppc') clampTo = 'ppc'
    registry = { count: consumers.size, clampTo, compare: compareConsumers.size > 0 }
  }

  // While anything is subscribed, a browser tab coming back into view also
  // catches up with the day (a page left on screen overnight never re-registers),
  // and a minute timer rolls the range over at Pacific midnight (C10) so a tab
  // left open and in view does not go stale either.
  const ROLLOVER_MS = 60_000
  let detachVisibility: (() => void) | null = null
  function watchVisibility(): void {
    if (detachVisibility) return
    const onChange = () => { if (typeof document === 'undefined' || document.visibilityState === 'visible') refreshDay() }
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onChange)
    const timer = setInterval(onChange, ROLLOVER_MS)
    detachVisibility = () => {
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onChange)
      clearInterval(timer)
    }
  }

  return {
    getCore,
    getRegistry: (): AppDateRangeRegistry => registry,
    subscribe(listener: () => void): () => void {
      listeners.add(listener)
      watchVisibility()
      return () => {
        listeners.delete(listener)
        if (listeners.size === 0 && detachVisibility) {
          detachVisibility()
          detachVisibility = null
        }
      }
    },
    /** A page's change (e.g. a KPI-tile click): applied for this visit only. */
    setRange(range: DateRange): void {
      setCore({ ...getCore(), range, day: today() })
    },
    /** The header's pick: applied and remembered (single-day picks are not
     *  remembered — DateRangePicker's rule). */
    pickRange(range: DateRange): void {
      persistRange(APP_RANGE_KEY, range)
      setCore({ ...getCore(), range, day: today() })
    },
    setCompare(setting: CompareSetting): void {
      compareChosen = true
      writeStoredCompare(setting)
      setCore({ ...getCore(), compare: setting })
    },
    /** True while nobody has picked a comparison: nothing stored and nothing set this session. */
    compareIsUnset,
    /** A page that has always compared (Ads overview) gives the untouched default a mode. Held in
     *  memory only, so the header shows it but it is never remembered as the person's own pick. */
    seedCompare(setting: CompareSetting): void {
      if (!compareIsUnset() || getCore().compare.mode === setting.mode) return
      setCore({ ...getCore(), compare: setting })
    },
    /** Called by each opted-in page while it is on screen; returns the
     *  unregister. Also the moment to catch up with a new calendar day. */
    register(id: string, opts: AppDateRangeOptions): () => void {
      consumers.set(id, opts)
      recomputeRegistry()
      refreshDay()
      emit()
      return () => {
        consumers.delete(id)
        recomputeRegistry()
        emit()
      }
    },
    /** Called by each component that draws the comparison while it is on
     *  screen (`useAppCompare`); returns the unregister. */
    registerCompare(id: string): () => void {
      compareConsumers.add(id)
      recomputeRegistry()
      emit()
      return () => {
        compareConsumers.delete(id)
        recomputeRegistry()
        emit()
      }
    },
    refreshDay,
  }
}

export type AppDateRangeStore = ReturnType<typeof createAppDateRangeStore>

const appDateRangeStore = createAppDateRangeStore()

/** The one store, for the URL sync (components/AppDateRangeUrlSync.tsx). */
export function getAppDateRangeStore(): AppDateRangeStore { return appDateRangeStore }

/**
 * Opt a page into the app-wide range. Same shape as `useDateRange` —
 * `[range, setRange, effective]` — plus the comparison as a fourth value, so
 * moving a page over is a one-line change. The page should stop rendering its
 * own `<DateRangePicker>`: while it is on screen the header shows the control.
 *
 * The fifth value, `ready` (PR 10, tracker B11), says `effective` has
 * SETTLED: with `clampTo` the window is unclamped until the PPC "data
 * through" day loads, then its `to` moves back to the last complete report
 * day. A page that fetches on the first render sends a request for the
 * unclamped window and cancels it a moment later; gating the fetch on `ready`
 * sends it once. True at once without `clampTo`; with it, once the freshness
 * read has answered (success or failure — a failed read leaves the window
 * unclamped for good, and waiting forever would be worse). Existing
 * four-value destructuring keeps working.
 */
export function useAppDateRange(
  opts?: UseAppDateRangeOptions,
): [DateRange, (r: DateRange) => void, EffectiveRange, AppCompare, boolean] {
  const store = appDateRangeStore
  const core = useSyncExternalStore(store.subscribe, store.getCore, store.getCore)
  const clampTo = opts?.clampTo
  const id = useId()
  const enabled = opts?.enabled ?? true
  useEffect(() => (enabled ? store.register(id, { clampTo }) : undefined), [store, id, clampTo, enabled])
  const ppc = usePpcFreshness()
  const dataThrough = ppc.data?.last_complete_date ?? null
  const ready = !clampTo || ppc.isFetched || ppc.isError
  // Two memos: a compare change must not hand the page a new `effective`.
  const effective = useMemo(
    () => effectiveWindow(core.range, clampTo, dataThrough),
    [core.range, clampTo, dataThrough],
  )
  const compare = useMemo(() => resolveCompare(effective, core.compare), [effective, core.compare])
  return [core.range, store.setRange, effective, compare, ready]
}

/** The account (Pacific) day the store last saw. It turns at midnight with the
 *  header range, so a component that keeps its own clock (the Campaigns day
 *  tiles) re-reads "now" when this changes and never describes a different day. */
export function useAppDay(): string {
  const store = appDateRangeStore
  return useSyncExternalStore(store.subscribe, () => store.getCore().day, () => store.getCore().day)
}

/** The app-wide comparison and its setter, for a control that DRAWS it (the
 *  Campaigns period tiles): changing it there changes the header. Calling
 *  this is what puts "compared to" in the header while the caller is on
 *  screen - a page that only reads the range never shows it. */
export function useAppCompare(
  opts?: { defaultSetting?: CompareSetting },
): [CompareSetting, (setting: CompareSetting) => void] {
  const store = appDateRangeStore
  const core = useSyncExternalStore(store.subscribe, store.getCore, store.getCore)
  const id = useId()
  const seed = opts?.defaultSetting
  useEffect(() => store.registerCompare(id), [store, id])
  useEffect(() => { if (seed) store.seedCompare(seed) }, [store, seed?.mode])   // eslint-disable-line react-hooks/exhaustive-deps
  // Right away on the first render, so the first request already carries it.
  const value = seed && core.compare.mode === 'none' && store.compareIsUnset() ? seed : core.compare
  return [value, store.setCompare]
}

/** The header control's view of the store: the pick, the setters, and
 *  whether any opted-in page is on screen (and whether it clamps to PPC). */
export function useAppDateRangeControl() {
  const store = appDateRangeStore
  const core = useSyncExternalStore(store.subscribe, store.getCore, store.getCore)
  const registry = useSyncExternalStore(store.subscribe, store.getRegistry, store.getRegistry)
  return {
    range: core.range,
    compare: core.compare,
    active: registry.count > 0,
    clampTo: registry.clampTo,
    /** Offer and show the comparison: something on screen draws it. */
    compareShown: registry.compare,
    pickRange: store.pickRange,
    setCompare: store.setCompare,
  }
}
